import type { MatrixData } from '../components/MatrixTable'
import { farmDayKey } from './farmTime'
import { interpolateFromMatrix } from './sfrMatrix'

// Findings #210: Protocolo de traslado. Fish are not fed before a stressful
// move and get a reduced ration after it: the ration for their biomass read at
// the water temperature minus Δ °C. All office-set, no migration:
// - `Farm.metadata.transferProtocol`  bands by weight for the facility's species
// - `Lot.metadata.plannedTransfer`    "Programar traslado": the move date and its band
//                                     (the phone shows "Ayuno hoy" on the fasting days)
// - `Lot.metadata.transferRation`     written by Trasladar lote: the reduced kg/día the
//                                     phone shows for N days in the destination unit
// - `FeedLog.metadata.transferRation` { movedOn, backToNormal: true } — "Ya comen normal"
//                                     (crew phones cannot edit lots, so the crew's own record carries it)

export type TransferProtocolBand = {
  /** Upper weight of the band (g); null = every heavier fish. */
  maxWeightG: number | null
  fastDays: number
  reducedDays: number
  deltaC: number
}

export type PlannedTransfer = { date: string; band: TransferProtocolBand; plannedAt: string }

export type TransferRation = {
  /** Farm day of the move (day 1 of the reduced ration). */
  movedOn: string
  productionUnitId: string
  band: TransferProtocolBand
  /** Farm days the lot was meant to fast (from the planned move). */
  fastingDays: string[]
  temperatureC: number | null
  reducedKgPerDay: number | null
  /** The temperature − Δ fell below the SFR table: extrapolated from its two lowest columns. */
  extrapolated: boolean
}

type WithMetadata = { metadata?: Record<string, unknown> | null } | null | undefined

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const DAY = /^\d{4}-\d{2}-\d{2}$/

const wholeAtLeastZero = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 30 ? parsed : null
}

const finiteAtLeastZero = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 20 ? parsed : null
}

const readBand = (raw: unknown): TransferProtocolBand | null => {
  if (!isPlainObject(raw)) return null
  const maxWeightG = raw.maxWeightG === null || raw.maxWeightG === undefined ? null : Number(raw.maxWeightG)
  if (maxWeightG !== null && (!Number.isFinite(maxWeightG) || maxWeightG <= 0)) return null
  const fastDays = wholeAtLeastZero(raw.fastDays)
  const reducedDays = wholeAtLeastZero(raw.reducedDays)
  const deltaC = finiteAtLeastZero(raw.deltaC)
  if (fastDays === null || reducedDays === null || deltaC === null) return null
  return { maxWeightG, fastDays, reducedDays, deltaC }
}

/** Bands lightest first; one open band ("más") at most, always last. */
export const sortBands = (bands: TransferProtocolBand[]): TransferProtocolBand[] => {
  const closed = bands.filter((band) => band.maxWeightG !== null).sort((a, b) => (a.maxWeightG as number) - (b.maxWeightG as number))
  const open = bands.find((band) => band.maxWeightG === null)
  return open ? [...closed, open] : closed
}

export const readTransferProtocol = (farm: WithMetadata): TransferProtocolBand[] => {
  const raw = isPlainObject(farm?.metadata) ? farm?.metadata?.transferProtocol : undefined
  if (!isPlainObject(raw) || !Array.isArray(raw.bands)) return []
  return sortBands(raw.bands.map(readBand).filter((band): band is TransferProtocolBand => band !== null))
}

export const withTransferProtocol = <T extends NonNullable<WithMetadata>>(farm: T, bands: TransferProtocolBand[]): T => {
  const metadata = { ...(isPlainObject(farm.metadata) ? farm.metadata : {}) }
  if (bands.length === 0) delete metadata.transferProtocol
  else metadata.transferProtocol = { bands: sortBands(bands).map((band) => ({ ...band })) }
  return { ...farm, metadata }
}

/** The band for a weight: the first whose upper weight it does not pass; -1 without a weight or bands. */
export const bandIndexForWeight = (bands: TransferProtocolBand[], weightG: number | null | undefined): number => {
  if (typeof weightG !== 'number' || !Number.isFinite(weightG) || weightG <= 0) return -1
  return bands.findIndex((band) => band.maxWeightG === null || weightG <= band.maxWeightG)
}

export const addDays = (day: string, days: number): string => (
  new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10)
)

/** Days from `from` to `to` (farm days, YYYY-MM-DD). */
export const daysBetween = (from: string, to: string): number => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000)

/** The fasting days before a move on `date`, earliest first. */
export const fastingDaysBefore = (date: string, fastDays: number): string[] => (
  Array.from({ length: Math.max(0, fastDays) }, (_, index) => addDays(date, index - fastDays))
)

export const readPlannedTransfer = (lot: WithMetadata): PlannedTransfer | null => {
  const raw = isPlainObject(lot?.metadata) ? lot?.metadata?.plannedTransfer : undefined
  if (!isPlainObject(raw) || typeof raw.date !== 'string' || !DAY.test(raw.date)) return null
  const band = readBand(raw.band)
  if (!band) return null
  return { date: raw.date, band, plannedAt: typeof raw.plannedAt === 'string' ? raw.plannedAt : '' }
}

export const withPlannedTransfer = <T extends NonNullable<WithMetadata>>(lot: T, plan: PlannedTransfer | null): T => {
  const metadata = { ...(isPlainObject(lot.metadata) ? lot.metadata : {}) }
  if (plan) metadata.plannedTransfer = { date: plan.date, band: { ...plan.band }, plannedAt: plan.plannedAt }
  else delete metadata.plannedTransfer
  return { ...lot, metadata }
}

export const readTransferRation = (lot: WithMetadata): TransferRation | null => {
  const raw = isPlainObject(lot?.metadata) ? lot?.metadata?.transferRation : undefined
  if (!isPlainObject(raw) || typeof raw.movedOn !== 'string' || !DAY.test(raw.movedOn)) return null
  if (typeof raw.productionUnitId !== 'string' || !raw.productionUnitId) return null
  const band = readBand(raw.band)
  if (!band) return null
  const kg = Number(raw.reducedKgPerDay)
  const temperature = Number(raw.temperatureC)
  return {
    movedOn: raw.movedOn,
    productionUnitId: raw.productionUnitId,
    band,
    fastingDays: Array.isArray(raw.fastingDays) ? raw.fastingDays.filter((day): day is string => typeof day === 'string' && DAY.test(day)) : [],
    temperatureC: raw.temperatureC !== null && raw.temperatureC !== undefined && Number.isFinite(temperature) ? temperature : null,
    reducedKgPerDay: raw.reducedKgPerDay !== null && raw.reducedKgPerDay !== undefined && Number.isFinite(kg) && kg > 0 ? kg : null,
    extrapolated: raw.extrapolated === true
  }
}

/** Saves the reduced ration on the lot that arrived, and clears its planned move. */
export const withTransferRation = <T extends NonNullable<WithMetadata>>(lot: T, ration: TransferRation | null): T => {
  const metadata = { ...(isPlainObject(lot.metadata) ? lot.metadata : {}) }
  delete metadata.plannedTransfer
  if (ration) metadata.transferRation = { ...ration, band: { ...ration.band }, fastingDays: [...ration.fastingDays] }
  else delete metadata.transferRation
  return { ...lot, metadata }
}

const sfrAtColumn = (matrix: MatrixData, weightG: number, column: number): number => {
  const weights = matrix.weights
  const value = (row: number) => matrix.values[row]?.[column] ?? 0
  if (weightG <= weights[0]) return value(0)
  const last = weights.length - 1
  if (weightG >= weights[last]) return value(last)
  const upper = weights.findIndex((weight) => weight >= weightG)
  const lower = upper - 1
  const ratio = (weightG - weights[lower]) / (weights[upper] - weights[lower])
  return value(lower) + ratio * (value(upper) - value(lower))
}

/**
 * Findings #210: SFR (%/day) at the calculation temperature. Inside the table it
 * is the table's own value; below its lowest temperature it is extrapolated in a
 * straight line from the two lowest temperature columns (never below 0).
 */
export const sfrForCalculationTemperature = (
  matrix: MatrixData,
  weightG: number,
  temperatureC: number,
  options?: { species?: string }
): { sfr: number; extrapolated: boolean } | null => {
  const temperatures = matrix.temperatures
  if (temperatures.length === 0 || matrix.weights.length === 0 || !Number.isFinite(weightG) || weightG <= 0) return null
  if (temperatureC >= temperatures[0] || temperatures.length < 2) {
    const sfr = interpolateFromMatrix(matrix, weightG, temperatureC, options).sfr
    return Number.isFinite(sfr) && sfr > 0 ? { sfr, extrapolated: false } : null
  }
  const s0 = sfrAtColumn(matrix, weightG, 0)
  const s1 = sfrAtColumn(matrix, weightG, 1)
  const slope = (s1 - s0) / (temperatures[1] - temperatures[0])
  const sfr = Math.max(0, s0 + slope * (temperatureC - temperatures[0]))
  return { sfr, extrapolated: true }
}

/** The reduced ration (kg/day, 1 decimal like the plan) for `fish` of `weightG` at `temperatureC − Δ`. */
export const reducedRationKg = (args: {
  matrix: MatrixData
  species?: string
  fish: number | null
  weightG: number | null
  temperatureC: number | null
  deltaC: number
}): { kgPerDay: number; extrapolated: boolean } | null => {
  const { fish, weightG, temperatureC } = args
  if (fish === null || weightG === null || temperatureC === null || fish <= 0 || weightG <= 0) return null
  const result = sfrForCalculationTemperature(args.matrix, weightG, temperatureC - args.deltaC, { species: args.species })
  if (!result) return null
  const biomassKg = (fish * weightG) / 1000
  const kgPerDay = Math.round(((biomassKg * result.sfr) / 100) * 1000) / 1000
  return { kgPerDay, extrapolated: result.extrapolated }
}

export type CrewTransferState =
  | { kind: 'fasting'; moveDate: string }
  | { kind: 'reduced'; day: number; of: number; kgPerDay: number | null; movedOn: string }

/** What the feed screen shows for a lot in a unit today, or null when nothing applies. */
export const crewTransferState = (args: {
  planned: PlannedTransfer | null
  ration: TransferRation | null
  productionUnitId: string
  today: string
  backToNormal: boolean
}): CrewTransferState | null => {
  const { planned, ration, today } = args
  if (planned && fastingDaysBefore(planned.date, planned.band.fastDays).includes(today)) {
    return { kind: 'fasting', moveDate: planned.date }
  }
  if (ration && ration.productionUnitId === args.productionUnitId && !args.backToNormal) {
    const day = daysBetween(ration.movedOn, today) + 1
    if (day >= 1 && day <= ration.band.reducedDays) {
      return { kind: 'reduced', day, of: ration.band.reducedDays, kgPerDay: ration.reducedKgPerDay, movedOn: ration.movedOn }
    }
  }
  return null
}

type FeedLogLike = { lotId?: string | null; recordedAt?: string | null; feedKg?: unknown; metadata?: unknown; productionUnitId?: string | null }

/** "Ya comen normal": a feed record of this lot since the move carries the flag. */
export const isBackToNormal = (logs: FeedLogLike[], lotId: string, movedOn: string, timeZone: string): boolean => logs.some((log) => {
  if (log.lotId !== lotId || !isPlainObject(log.metadata)) return false
  const flag = log.metadata.transferRation
  if (!isPlainObject(flag) || flag.backToNormal !== true || flag.movedOn !== movedOn) return false
  const day = farmDayKey(log.recordedAt, timeZone)
  return Boolean(day && day >= movedOn)
})

export const backToNormalMetadata = (movedOn: string): Record<string, unknown> => ({ transferRation: { movedOn, backToNormal: true } })

/** The fasting days with the feed recorded for the lot on each (0 = fasted as planned). */
export const fastingRecord = (days: string[], logs: FeedLogLike[], lotId: string, timeZone: string): Array<{ day: string; fedKg: number }> => days.map((day) => ({
  day,
  fedKg: Math.round(logs
    .filter((log) => log.lotId === lotId && farmDayKey(log.recordedAt, timeZone) === day)
    .reduce((total, log) => total + (Number.parseFloat(String(log.feedKg ?? '')) || 0), 0) * 1000) / 1000
}))
